import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Option,
  Select,
  selectClasses,
} from "@mui/joy";
import { BASE_URL, BLACK, PRIMARY, WHITE } from "../../appConstants";
import PaginationComponent from "../commonComponents/PaginationComponent";
import { useSelector } from "react-redux";
import axios from "axios";
import appError from "../../appError";
import CreateNewCategoryModal from "./CreateNewCategoryModal";
import UpdateCategoryModal from "./UpdateCategoryModal";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";

type FilterStateObj = {
  limit: number;
  page: number;
};
type Main_category = {
  brand_id: string;
  created_by: string;
  created_time_stamp: string;
  main_category: string;
  main_category_id: string;
  status: string;
};

const MainCategoryMaster: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [filterState, setFilterState] = useState<FilterStateObj>({
    limit: 10,
    page: 1,
  });
  const [dataFetchingState, setDataFetchingState] = useState<string>("");
  const {brand_id} = useParams();
  const [categories_arr, setCategories_arr] = useState<Main_category[]>([]);
  const [editToggle, setEditToggle] = useState<boolean>(false);
  const [loadingToggle, setLoadingToggle] = useState<boolean>(false);

  type EditDataProps = {
    cat_type_id: string;
    cat_type_name: string;
  }
  const [editData, setEditData] = useState<EditDataProps>({} as EditDataProps);

  const fetchprincipleCatdata = async () => {
    const token = localStorage.getItem("token");
    try {
      setDataFetchingState("LOADING");
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      let api = `${BASE_URL}categorymaster/category?brand_id=${brand_id}&cat_type_name=MAIN&limit=${filterState?.limit}&page=${filterState?.page}`;
      const result = await axios.get(api, { headers });
      const { total_records, category_array } = result?.data?.data;
      if (category_array?.length === 0) {
        setDataFetchingState("EMPTY");
      } else {
        setTotalRows(total_records);
        setCategories_arr(category_array);
        setDataFetchingState("SUCCESS");
      }
    } catch (error) {
      appError(error);
    }
  };

  useEffect(() => {
    if (brand_id) {
      fetchprincipleCatdata();
    }
  }, [brand_id, filterState]);

  const handleExportEmptyExcel = () => {
    // Create an empty row with just the column name
    const data = [{ main_category: "" }];
  
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
  
    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    saveAs(fileData, `main_category_${brand_id}.xlsx`);
  };

  const callImportAPI = async (ag_main_categories: string[]) => {
    try {
      setLoadingToggle(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${BASE_URL}categorymaster/exportCategoryExcel?brand_id=${brand_id}&cat_type=MAIN`, {
          cat_type_name_arr: ag_main_categories
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const {status, data } = res?.data;

      if(status === "success"){
        fetchprincipleCatdata();
        toast.success(data?.message);
      }

    } catch (error) {
      appError(error);
    } finally {
      setLoadingToggle(false);
    }
  }
  
  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
  
    const reader = new FileReader();
  
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
  
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
  
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const mainCategories = jsonData.map((row: any) => String(row.main_category).trim());
  
      callImportAPI(mainCategories);
    };
  
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <div
        className="flex flex-row justify-between p-6"
        style={{
          height: 96,
          backgroundColor: PRIMARY.P_Purple50,
          borderRadius: 8,
        }}
      >
        <div className="flex flex-col justify-between">
          <div
            style={{
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "24px",
              color: BLACK.B_100,
            }}
          >
            Add New Main Category
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "18px",
              color: BLACK.B_40,
            }}
          >
            Provide the information for the new main category here
          </div>
        </div>

        <div className="flex gap-4">
          <input
            type="file"
            accept=".xlsx, .xls"
            id="excelFile"
            style={{ display: "none" }}
            onChange={handleImportExcel}
          />
  
          <Button
            onClick={() => document.getElementById("excelFile")?.click()}
            startDecorator={
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                <path d="M8.00002 0.750041L8.00002 9.08337M8.00002 0.750041C7.4165 0.750041 6.32629 2.41196 5.91669 2.83337M8.00002 0.750041C8.58354 0.750041 9.67375 2.41196 10.0834 2.83337" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M14.6667 10.75C14.6667 12.8183 14.235 13.25 12.1667 13.25H3.83334C1.76501 13.25 1.33334 12.8183 1.33334 10.75" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            }
            sx={{
              height: "48px",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "20px",
              backgroundColor: PRIMARY.P_BLue,
              textWrap: "nowrap",
            }}
            loading={loadingToggle}
          >
            Upload
          </Button>
  
          <Button
            // loading={lodingOnSave}
            onClick={() => handleExportEmptyExcel()}
            startDecorator={
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 11.5L9 1.5M9 11.5C8.29977 11.5 6.99153 9.5057 6.5 9M9 11.5C9.70023 11.5 11.0085 9.5057 11.5 9" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M17 13.5C17 15.982 16.482 16.5 14 16.5H4C1.518 16.5 1 15.982 1 13.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            }
            sx={{
              height: "48px",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "20px",
              backgroundColor: PRIMARY.P_BLue,
              textWrap:'nowrap',
            }}
          >
            Export
          </Button>
  
          <Button
          // loading={lodingOnSave}
          onClick={() => setOpenModal(true)}
          startDecorator={
          <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          >
          <path
          d="M9.16663 10.8333H4.99996C4.76385 10.8333 4.56593 10.7534 4.40621 10.5937C4.24649 10.434 4.16663 10.2361 4.16663 9.99996C4.16663 9.76385 4.24649 9.56593 4.40621 9.40621C4.56593 9.24649 4.76385 9.16663 4.99996 9.16663H9.16663V4.99996C9.16663 4.76385 9.24649 4.56593 9.40621 4.40621C9.56593 4.24649 9.76385 4.16663 9.99996 4.16663C10.2361 4.16663 10.434 4.24649 10.5937 4.40621C10.7534 4.56593 10.8333 4.76385 10.8333 4.99996V9.16663H15C15.2361 9.16663 15.434 9.24649 15.5937 9.40621C15.7534 9.56593 15.8333 9.76385 15.8333 9.99996C15.8333 10.2361 15.7534 10.434 15.5937 10.5937C15.434 10.7534 15.2361 10.8333 15 10.8333H10.8333V15C10.8333 15.2361 10.7534 15.434 10.5937 15.5937C10.434 15.7534 10.2361 15.8333 9.99996 15.8333C9.76385 15.8333 9.56593 15.7534 9.40621 15.5937C9.24649 15.434 9.16663 15.2361 9.16663 15V10.8333Z"
          fill="white"
          />
          </svg>
          }
          sx={{
          height: "48px",
          fontWeight: 500,
          fontSize: 14,
          lineHeight: "20px",
          backgroundColor: PRIMARY.P_BLue,
          textWrap:'nowrap'
          }}
          >
          Add New Main Category
          </Button>
        </div>
      </div>

      <div
        className=" mt-6"
        style={{
          height: "calc(100vh - 350px)",
          borderRadius: 12,
          boxShadow: "0px 4px 10px 4px rgba(130, 130, 130, 0.05)",
        }}
      >
        <div
          className="flex items-center w-full"
          style={{
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            height: 56,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: "20px",
            backgroundColor: PRIMARY.P_Purple50,
          }}
        >
          <div className="p-4" style={{ width: "50%" }}>
            Main Category
          </div>
          <div className="p-4 mr-4 flex justify-end" style={{ width: "50%" }}>
            Edit
          </div>
        </div>

        {dataFetchingState === "LOADING" && (
          <div
            className="flex justify-center items-center w-full"
            style={{
              // position: "absolute",
              height: "calc(100vh - 466px)",
            }}
          >
            <CircularProgress />
          </div>
        )}

        {dataFetchingState === "EMPTY" && (
          <div
            className="flex flex-col justify-center items-center py-6 self-stretch"
            style={{
              height: "calc(100vh - 466px)",
            }}
          >
            <img
              height={220}
              width={220}
              className="shrink-0"
              src={`${process.env.PUBLIC_URL}/ic-content.png`}
              alt="Empty"
            />
            <div
              className="self-stretch"
              style={{
                textAlign: "center",
                fontSize: "18px",
                fontWeight: "400",
                lineHeight: "24px",
                color: "#9D9D9D",
              }}
            >
              No results found.
            </div>
          </div>
        )}

        {dataFetchingState === "SUCCESS" && (
          <div
            style={{ height: "calc(100vh - 466px)" }}
            className="overflow-y-auto"
          >
            {categories_arr.length > 0 &&
              categories_arr?.map((category) => {
                return (
                  <>
                    <div
                      className="flex items-center w-full"
                      key={category?.main_category_id}
                      style={{
                        height: 52,
                        borderBottom: `1px solid ${BLACK.B_5}`,
                        color: BLACK.B_80,
                      }}
                    >
                      <div
                        className="flex p-4 items-center self-stretch gap-2"
                        style={{ width: "50%" }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            fontStyle: "normal",
                            lineHeight: "18px",
                            color: BLACK.B_80,
                          }}
                        >
                          {category?.main_category}
                        </div>
                      </div>

                      <div
                        className="flex p-4 justify-end items-center gap-[4px] self-stretch cursor-pointer"
                        style={{ width: "50%" }}
                        onClick={() => {
                          setEditData({
                            cat_type_id: category?.main_category_id,
                            cat_type_name: category?.main_category
                          });
                          setEditToggle(true);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M4.16667 15.8333H5.35417L13.5 7.6875L12.3125 6.5L4.16667 14.6458V15.8333ZM3.33333 17.5C3.09722 17.5 2.89931 17.4201 2.73958 17.2604C2.57986 17.1007 2.5 16.9028 2.5 16.6667V14.6458C2.5 14.4236 2.54167 14.2118 2.625 14.0104C2.70833 13.809 2.82639 13.6319 2.97917 13.4792L13.5 2.97917C13.6667 2.82639 13.8507 2.70833 14.0521 2.625C14.2535 2.54167 14.4653 2.5 14.6875 2.5C14.9097 2.5 15.125 2.54167 15.3333 2.625C15.5417 2.70833 15.7222 2.83333 15.875 3L17.0208 4.16667C17.1875 4.31944 17.309 4.5 17.3854 4.70833C17.4618 4.91667 17.5 5.125 17.5 5.33333C17.5 5.55556 17.4618 5.76736 17.3854 5.96875C17.309 6.17014 17.1875 6.35417 17.0208 6.52083L6.52083 17.0208C6.36806 17.1736 6.19097 17.2917 5.98958 17.375C5.78819 17.4583 5.57639 17.5 5.35417 17.5H3.33333ZM12.8958 7.10417L12.3125 6.5L13.5 7.6875L12.8958 7.10417Z"
                            fill="#1C1C1C"
                            fill-opacity="0.8"
                          />
                        </svg>
                      </div>
                    </div>
                  </>
                );
              })}
          </div>
        )}

        <div
          className="flex justify-between items-center px-4"
          style={{
            borderTop: `1px solid ${BLACK.B_5}`,
            height: 60,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <div className="flex items-center">
            <div
              className="mr-2"
              style={{ fontSize: 12, fontWeight: 400, lineHeight: "18px" }}
            >
              Rows per page:
            </div>
            <Select
              onChange={(e, limit) =>
                setFilterState((prevState) => ({
                  ...prevState,
                  page: 1,
                  limit: limit ? limit : 0 * 1,
                }))
              }
              // placeholder="Select State"
              name="status"
              value={filterState.limit}
              indicator={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M9.56251 12.0625L6.54167 9.04171C6.50001 9.00004 6.46876 8.9549 6.44792 8.90629C6.42709 8.85768 6.41667 8.8056 6.41667 8.75004C6.41667 8.63893 6.45487 8.54171 6.53126 8.45837C6.60764 8.37504 6.70834 8.33337 6.83334 8.33337H13.1667C13.2917 8.33337 13.3924 8.37504 13.4688 8.45837C13.5451 8.54171 13.5833 8.63893 13.5833 8.75004C13.5833 8.77782 13.5417 8.87504 13.4583 9.04171L10.4375 12.0625C10.3681 12.132 10.2986 12.1806 10.2292 12.2084C10.1597 12.2362 10.0833 12.25 10 12.25C9.91667 12.25 9.84028 12.2362 9.77084 12.2084C9.70139 12.1806 9.63195 12.132 9.56251 12.0625Z"
                    fill="#1C1C1C"
                    fill-opacity="0.8"
                  />
                </svg>
              }
              sx={{
                "--Input-focusedThickness": "0px",
                width: "80px",
                height: "28px",
                backgroundColor: WHITE.W_100,
                color: BLACK.B_80,
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "20px",
                border: `1px solid ${BLACK.B_10}`,
                [`& .${selectClasses.indicator}`]: {
                  transition: "0.2s",
                  [`&.${selectClasses.expanded}`]: {
                    transform: "rotate(-180deg)",
                  },
                },
              }}
            >
              <Option value={10}>10</Option>
              <Option value={20}>20</Option>
              <Option value={50}>50</Option>
            </Select>
          </div>
          {/* ---------------------Pagination Component------------------ */}
          <PaginationComponent
            page={filterState.page}
            limit={filterState.limit}
            totalRows={totalRows}
            onChange={(page) =>
              setFilterState((prevState) => ({ ...prevState, page }))
            }
          />
          {/* ---------------------Pagination Component------------------ */}
        </div>
      </div>
      {
        openModal && (
          <CreateNewCategoryModal setOpenModal={setOpenModal} cat_type="Main Category" fetchCatdata={fetchprincipleCatdata}/>
        )
      }
      {
        editToggle && (
          <UpdateCategoryModal setModalToggle={setEditToggle} cat_type="Main Category" fetchCatdata={fetchprincipleCatdata} editData={editData}/>
        )
      }
    </>
  );
};

export default MainCategoryMaster;